<script setup>
import { ref, computed } from "vue";
import PriceOverviewCard from "./PriceOverviewCard.vue";
import { useClickOutside } from "../../../../composables/useClickOutside";
import { useTranslations } from "@/composables/translations";
import { useAppUi } from '@/stores/app-ui'

const { translate } = useTranslations();

const appUiStore = useAppUi();

const isOpen = ref(false);
const boxRef = ref(null);

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  priceOverview: {
    type: Object,
    required: true,
  },
});

const isFreeTicket = computed(() => {
  return props.priceOverview.total && props.priceOverview.total === "€ 0,00";
});

const openOverview = () => {
  isOpen.value = !isOpen.value;
  appUiStore.setScrollToTop(!isOpen.value);
};

useClickOutside(boxRef, () => {
  if (isOpen.value) isOpen.value = false;
});
</script>

<template>
  <!-- Mobile view -->
  <div v-if="mobile" class="overview-mobile-wrapper" ref="boxRef">
    <transition name="fade-slide">
      <div
        v-if="isOpen"
        class="overview-mobile-box scroll-hidden"
        style="max-height: 420px"
        aria-hidden="false"
      >
        <PriceOverviewCard :ticket="ticket" :priceOverview="priceOverview" />
      </div>
    </transition>
    <div
      class="d-flex justify-content-between"
      :style="`background-color: ${colors(1)}; padding: 12px 27px`"
    >
      <div class="d-flex" style="gap: 5px">
        <h4 class="m-0" style="color: white">
          {{ !isFreeTicket ? translate("price_overview_total") : isOpen ? '' : translate("price_overview") }}
        </h4>
      </div>
      <div class="d-flex" style="gap: 5px">
        <span
          role="button"
          tabindex="0"
          class="m-0"
          @click="openOverview"
          @keydown.enter="openOverview"
          aria-expanded="isOpen"
          :aria-label="translate('price_overview_toggle_overview')"
        >
          <i
            :class="{ 'icon-arrow-up': !isOpen, 'icon-arrow-down': isOpen }"
            style="color: white; font-size: 22px"
          ></i>
        </span>
        <h4 v-if="!isFreeTicket" class="m-0" style="color: white">{{ priceOverview.total }}</h4>
      </div>
    </div>
  </div>

  <!-- Desktop view -->
  <PriceOverviewCard v-else :ticket="ticket" :priceOverview="priceOverview" />
</template>

<style lang="scss" scoped>
span:focus-visible {
  outline: 2px dotted !important;
  outline-offset: 3px;
}

.overview-mobile-wrapper {
  position: fixed;
  bottom: 0px;
  left: 0px;
  z-index: 1050;
  width: 100%;
}

.overview-mobile-box {
  background-color: #e5f7f1;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.scroll-hidden {
  overflow: auto;

  // Hide scrollbar for WebKit
  &::-webkit-scrollbar {
    display: none;
  }

  // Hide scrollbar for Firefox
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE & Edge Legacy
}
</style>
